import type { ComputedRef, InjectionKey } from "vue";
import { computed, shallowRef } from "vue";

export type DialogLabelKind = "title" | "description";

export interface DialogLabelClaim {
  /** The id to render on the claiming element, or `undefined` while another element names the dialog. */
  id: () => string | undefined
  release: () => void
}

/**
 * Lets the content of a dialog name it: the first present title becomes `aria-labelledby`, the first present
 * description `aria-describedby`. A headless dialog has no shell title, so without this it has no accessible name.
 */
export interface DialogLabels {
  titleId: ComputedRef<string | undefined>
  descriptionId: ComputedRef<string | undefined>
  claim: (kind: DialogLabelKind, present?: () => boolean) => DialogLabelClaim
}

export const DIALOG_LABELS_KEY: InjectionKey<DialogLabels> = Symbol("dialogLabels");

interface ClaimEntry {
  token: symbol
  present: () => boolean
}

export function createDialogLabels(baseId: string, titleClaimable: () => boolean): DialogLabels {
  const claims = {
    title: shallowRef<ClaimEntry[]>([]),
    description: shallowRef<ClaimEntry[]>([]),
  };
  const ids = {
    title: `${baseId}-title`,
    description: `${baseId}-description`,
  };

  const owners = {
    title: computed(() => titleClaimable() ? claims.title.value.find(entry => entry.present())?.token : undefined),
    description: computed(() => claims.description.value.find(entry => entry.present())?.token),
  };

  function claim(kind: DialogLabelKind, present: () => boolean = () => true): DialogLabelClaim {
    const token = Symbol(kind);
    claims[kind].value = [...claims[kind].value, { token, present }];

    return {
      id: () => (owners[kind].value === token ? ids[kind] : undefined),
      release: () => {
        claims[kind].value = claims[kind].value.filter(entry => entry.token !== token);
      },
    };
  }

  return {
    titleId: computed(() => (owners.title.value ? ids.title : undefined)),
    descriptionId: computed(() => (owners.description.value ? ids.description : undefined)),
    claim,
  };
}
